import formConfig from '../on-form-config.json';
import type { FieldDef, SectionDef, RefDataItem } from '../../shared/composables/useFormRenderer';
import { useFormRenderer } from '../../shared/composables/useFormRenderer';

export type { FieldDef, SectionDef, RefDataItem };

// ===== Types =====

export interface SectionTypeOption {
  value: string;
  label: string;
  action: string;
  officerType: string;
}

export interface ActionConfig {
  title: string;
  sections: SectionDef[];
}

// ===== Composable =====
export function useFormConfig() {
  const { sharedRefData, sharedTemplates, resolveTemplate: _resolveTemplate, resolveGroup: _resolveGroup, getRefOptions } = useFormRenderer();

  // Merge: shared templates as base, ON-specific templates override
  const onTemplates = formConfig.templates as Record<string, FieldDef[]>;
  const templates: Record<string, FieldDef[]> = { ...sharedTemplates, ...onTemplates };

  const enums = formConfig.enums as {
    sectionTypes: SectionTypeOption[];
    designations: { value: string; label: string }[];
    reasonsForCessation: { value: string; label: string }[];
    businessOccupations: { value: string; label: string }[];
  };

  const actions = formConfig.actions as Record<string, ActionConfig>;

  const refData: Record<string, RefDataItem[]> = { ...sharedRefData };

  /**
   * Resolve template ref to field array
   */
  function resolveTemplate(ref: string, includeOptional?: boolean): FieldDef[] {
    return _resolveTemplate(templates, ref, includeOptional);
  }

  /**
   * Resolve a template group into fields
   */
  function resolveGroup(group: { ref?: string; fields?: FieldDef[]; includeOptional?: boolean; excludeFields?: string[]; overrides?: Record<string, Partial<FieldDef>> }): FieldDef[] {
    return _resolveGroup(templates, group);
  }

  /**
   * Get sections for a given action type
   */
  function getActionSections(action: string): SectionDef[] {
    return (actions[action]?.sections as SectionDef[]) || [];
  }

  /**
   * Get action title
   */
  function getActionTitle(action: string): string {
    return actions[action]?.title || '';
  }

  /**
   * Get enum options by key
   */
  function getEnumOptions(key: string): { value: string; label: string }[] {
    return (enums as any)[key] || [];
  }

  /**
   * Find section type option by value
   */
  function getSectionType(value: string): SectionTypeOption | undefined {
    return enums.sectionTypes.find(s => s.value === value);
  }

  return {
    formConfig,
    templates,
    enums,
    actions,
    refData,
    resolveTemplate,
    resolveGroup,
    getRefOptions,
    getActionSections,
    getActionTitle,
    getEnumOptions,
    getSectionType,
  };
}
